import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { printOutline, arrowForward } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import type { Recipe, RecipeIngredient } from '@trackeat/shared';
import { computeRecipeConsumption, collettivaDietLabel } from '@trackeat/shared';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useMasters } from '../../lib/data/queries.js';
import { getSupabase } from '../../lib/supabase.js';
import { openExternalFromAsync } from '../../lib/openExternalUrl.js';

// T-348 — "Comanda di produzione" (comanda CUCINA, lato chef). Rimpiazza il
// vecchio flusso "stampa etichette per piatto" (che non serviva): la comanda è
// la lista dei piatti del giorno CON le rispettive ricette/ingredienti (lo
// stesso contenuto della stampa "totali cucina", edge `export-produzione-cucina`),
// così lo chef vede a schermo quello che vedrebbe sul PDF. Da qui:
//  - stampa la comanda cucina per servizio;
//  - passa allo "Scarico merce" per scaricare le materie prime usate.
// Lo spunta ✓ è un semplice check di avanzamento (piatto prodotto).

interface PlanItem {
  id: string;
  meal_service: string;
  output_name: string;
  category: string | null;
  recipe_id: string | null;
  planned_portions: number;
  status: 'pending' | 'done';
  diet_code: string | null; // T-441: dieta della riga (distingue base/varianti)
}
type RecipeRow = Pick<Recipe, 'id' | 'name'> & {
  ingredients?: RecipeIngredient[];
  // T-441: se valorizzato, la ricetta risolta è una VARIANTE (ricetta figlia).
  variant_of_recipe_id?: string | null;
};
type FoodRow = { id: string; name: string };
const MEAL_LABEL: Record<string, string> = { pranzo: 'Pranzo', cena: 'Cena', colazione: 'Colazione' };
const CAT_COLOR: Record<string, { bg: string; fg: string }> = {
  PRIMI: { bg: '#dbeafe', fg: '#1d4ed8' },
  SECONDI: { bg: '#fee2e2', fg: '#b91c1c' },
  CONTORNI: { bg: '#dcfce7', fg: '#15803d' },
  FRUTTA: { bg: '#fef9c3', fg: '#a16207' },
};
const CATEGORY_ORDER = ['PRIMI', 'SECONDI', 'CONTORNI', 'FRUTTA', 'ALTRO'];
const todayIso = (): string => new Date().toISOString().slice(0, 10);

function fmtQty(qty: number, uom: string): string {
  if (uom === 'g' && qty >= 1000) return `${(qty / 1000).toLocaleString('it', { maximumFractionDigits: 2 })} kg`;
  return `${qty.toLocaleString('it', { maximumFractionDigits: 3 })} ${uom}`;
}

export default function ProductionTodoPage(): JSX.Element {
  const { profile } = useAuth();
  const history = useHistory();
  const sb = useMemo(() => getSupabase(), []);
  const { data: recipes = [] } = useMasters<RecipeRow>('recipes');
  const { data: foods = [] } = useMasters<FoodRow>('foods');
  const [day, setDay] = useState<string>(todayIso());
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<PlanItem[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [printing, setPrinting] = useState<string | null>(null);

  const recipeById = useMemo(() => {
    const m = new Map<string, RecipeRow>();
    recipes.forEach((r) => m.set(r.id, r));
    return m;
  }, [recipes]);
  const foodName = useMemo(() => {
    const m = new Map<string, string>();
    foods.forEach((f) => m.set(f.id, f.name));
    return m;
  }, [foods]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await sb
      .from('production_plan_items')
      .select('id,meal_service,output_name,category,recipe_id,planned_portions,status,diet_code')
      .eq('service_date', day)
      .is('archived_at', null)
      .order('meal_service')
      .order('category');
    setItems((data as PlanItem[] | null) ?? []);
    setLoading(false);
  }, [sb, day]);

  useEffect(() => {
    void load();
  }, [load]);

  // T-406: rimossa la spunta "prodotto" — gli chef in produzione non segnano
  // sul tablet (mani sporche, confusione). La comanda è di sola lettura.

  // Stampa la comanda cucina (totali per piatto, senza destinazioni) del servizio.
  const printComanda = useCallback(
    async (meal: string) => {
      if (!profile?.active_tenant_id) { setToast('Manca tenant attivo.'); return; }
      setPrinting(meal);
      try {
        const res = await openExternalFromAsync(async () => {
          const { data, error } = await sb.functions.invoke<{ pdf_url?: string }>('export-produzione-cucina', {
            body: {
              tenant_id: profile.active_tenant_id,
              service_date: day,
              meal_service: meal,
              business_id: profile.active_business_id ?? undefined,
            },
          });
          if (error) throw error;
          if (!data?.pdf_url) throw new Error('Nessun PDF restituito');
          return data.pdf_url;
        });
        setToast(res.blocked ? 'Comanda pronta — tocca "Apri PDF"' : 'Comanda cucina generata');
        if (res.blocked && res.url) window.open(res.url, '_blank', 'noopener,noreferrer');
      } catch (e) {
        setToast(`Errore stampa: ${e instanceof Error ? e.message : String(e)}`);
      } finally {
        setPrinting(null);
      }
    },
    [sb, profile, day],
  );

  const byMeal = useMemo(() => {
    const m: Record<string, PlanItem[]> = {};
    for (const it of items) (m[it.meal_service] ??= []).push(it);
    return m;
  }, [items]);

  function ingredientsFor(it: PlanItem): { food_id: string; qty: number; uom: string }[] {
    if (!it.recipe_id || it.planned_portions <= 0) return [];
    const recipe = recipeById.get(it.recipe_id);
    if (!recipe?.ingredients) return [];
    return computeRecipeConsumption(recipe.ingredients, it.planned_portions);
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/today" /></IonButtons>
          <IonTitle>Comanda di produzione</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
        <div style={{ padding: '12px 16px 96px' }}>
          <p style={{ fontSize: 13, color: '#475569', margin: '0 4px 10px' }}>
            I piatti da produrre oggi con le loro ricette (come la stampa &quot;totali cucina&quot;). Stampa la
            comanda per il servizio e vai allo <strong>Scarico merce</strong>.
          </p>
          <IonList inset style={{ marginInline: 0 }}>
            <IonItem>
              <IonLabel position="stacked">Giorno</IonLabel>
              <IonInput type="date" value={day} onIonInput={(e) => setDay(String(e.detail.value ?? todayIso()))} />
            </IonItem>
          </IonList>

          <IonButton expand="block" fill="outline" style={{ margin: '4px 0 8px' }} onClick={() => history.push('/warehouse/production-sheet')}>
            Vai allo Scarico merce
            <IonIcon icon={arrowForward} slot="end" />
          </IonButton>

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}><IonSpinner /></div>
          ) : items.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 14, padding: 16, color: '#64748b', fontSize: 13 }}>
              Nessun piatto pianificato per questo giorno. Il responsabile lo crea da &quot;Pianifica produzione&quot;.
            </div>
          ) : (
            <>
              {Object.entries(byMeal).map(([meal, list]) => {
                const portions = list.reduce((a, x) => a + (x.planned_portions || 0), 0);
                // T-406: raggruppa per CATEGORIA (Primi/Secondi/…) come l'ordine
                // preposto — più leggibile per lo staff in cucina.
                const byCat = new Map<string, PlanItem[]>();
                for (const it of list) {
                  const c = it.category ?? 'ALTRO';
                  const arr = byCat.get(c) ?? [];
                  arr.push(it); byCat.set(c, arr);
                }
                const cats = [
                  ...CATEGORY_ORDER.filter((c) => byCat.has(c)),
                  ...[...byCat.keys()].filter((c) => !CATEGORY_ORDER.includes(c as never)),
                ];
                return (
                  <div key={meal} style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '8px 4px 8px', gap: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {MEAL_LABEL[meal] ?? meal} · {list.length} piatti · {portions} porz.
                      </div>
                      <IonButton size="small" fill="clear" disabled={printing === meal} onClick={() => { void printComanda(meal); }}>
                        {printing === meal ? <IonSpinner name="dots" /> : (<><IonIcon icon={printOutline} slot="start" />Stampa</>)}
                      </IonButton>
                    </div>
                    {cats.map((cat) => {
                      const col = CAT_COLOR[cat] ?? { bg: '#f1f5f9', fg: '#475569' };
                      return (
                        <div key={cat} style={{ marginBottom: 10 }}>
                          <div style={{ display: 'inline-block', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', background: col.bg, color: col.fg, borderRadius: 8, padding: '3px 10px', margin: '2px 4px 6px' }}>
                            {cat}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {(byCat.get(cat) ?? []).map((it) => {
                              const ings = ingredientsFor(it);
                              // T-441 — badge dieta della riga. La ricetta risolta per id
                              // (it.recipe_id) è già la VARIANTE quando definita. Casi:
                              //  - resolved undefined (variante non ancora sincronizzata) → "in sincronizzazione"
                              //  - resolved è una BASE ma la riga ha una dieta → variante non definita ("da definire")
                              //  - resolved è una variante (variant_of_recipe_id != null) → badge dieta normale
                              const resolved = it.recipe_id ? recipeById.get(it.recipe_id) : undefined;
                              const dietSyncing = it.diet_code != null && it.recipe_id != null && resolved === undefined;
                              const dietPending =
                                it.diet_code != null && resolved !== undefined && resolved.variant_of_recipe_id == null;
                              const dietBadge = it.diet_code
                                ? dietSyncing
                                  ? '⚠ in sincronizzazione'
                                  : dietPending
                                    ? `⚠ ${collettivaDietLabel(it.diet_code)} — variante da definire`
                                    : collettivaDietLabel(it.diet_code)
                                : null;
                              const dietBadgeWarn = dietSyncing || dietPending;
                              return (
                                <div key={it.id} style={{ background: '#fff', borderRadius: 14, padding: '12px 14px', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
                                  <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>{it.output_name}</div>
                                  {dietBadge && (
                                    <div
                                      style={{
                                        display: 'inline-block',
                                        marginTop: 5,
                                        fontSize: 11,
                                        fontWeight: 800,
                                        letterSpacing: '0.02em',
                                        borderRadius: 8,
                                        padding: '3px 9px',
                                        background: dietBadgeWarn ? '#fef3c7' : '#ede9fe',
                                        color: dietBadgeWarn ? '#b45309' : '#6d28d9',
                                        border: `1px solid ${dietBadgeWarn ? '#fcd34d' : '#ddd6fe'}`,
                                      }}
                                    >
                                      {dietBadge}
                                    </div>
                                  )}
                                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{it.planned_portions} porzioni</div>
                                  {ings.length > 0 ? (
                                    <div style={{ marginTop: 10, borderTop: '1px solid #f1f5f9', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                      {ings.map((g) => (
                                        <div key={g.food_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13 }}>
                                          <span style={{ color: '#334155', minWidth: 0, wordBreak: 'break-word' }}>{foodName.get(g.food_id) ?? g.food_id.slice(0, 8)}</span>
                                          <span style={{ color: '#0f172a', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtQty(g.qty, g.uom)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <IonNote style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                                      {it.recipe_id ? 'Ricetta senza ingredienti quantificati' : 'Piatto senza ricetta collegata'}
                                    </IonNote>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </>
          )}
        </div>
        <IonToast isOpen={toast !== null} message={toast ?? ''} duration={2500} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
}
